/**
 * Shot-list import: upload or paste the list, the worker aligns every cue in its voiceover and lays
 * out scenes, clips and elements; review the result, then apply it as one undoable step.
 */

import type { AudioClip, Element, Scene } from "@studio/engine";
import { Loader2, TriangleAlert, X } from "lucide-react";
import { useEffect, useState } from "react";
import { api, fileUrl, type Job } from "../api";
import { flush } from "./save";
import { useEditor } from "./store";

interface ImportResult {
  scenes: Scene[];
  elements: Element[];
  audio: AudioClip[];
  duration: number;
  report: { number: string; audio: string | null; cue: string; start: number; duration: number; score: number | null; image: string | null; build: boolean }[];
  warnings: string[];
  missingImages: string[];
  lowConfidence: string[];
}

export function ImportDialog() {
  const project = useEditor((s) => s.project)!;
  const [text, setText] = useState("");
  const [filename, setFilename] = useState("");
  const [align, setAlign] = useState(true);
  const [gap, setGap] = useState(project.defaults.gapBetweenAudio);
  const [job, setJob] = useState<Job | null>(null);
  const [result, setResult] = useState<ImportResult | null>(null);
  const [err, setErr] = useState("");
  const close = () => useEditor.getState().set({ panel: null });
  const audioNames = new Set(project.assets.filter((a) => a.kind === "audio").map((a) => a.name.replace(/\.[^.]+$/, "").toLowerCase()));

  // poll the job until it finishes, then fetch the proposal
  useEffect(() => {
    if (!job || !["queued", "running"].includes(job.status)) return;
    const t = window.setTimeout(async () => {
      try {
        const j = await api.job(job.id);
        setJob(j);
        if (j.status === "done" && j.output?.file) setResult(await (await fetch(fileUrl(project.id, j.output.file))).json());
        if (j.status === "error") setErr(j.message);
      } catch (e) {
        setErr((e as Error).message);
      }
    }, 800);
    return () => window.clearTimeout(t);
  }, [job, project.id]);

  const start = async () => {
    setErr("");
    setResult(null);
    try {
      await flush();
      setJob(await api.importShotlist(project.id, { text, filename, align, gap }));
    } catch (e) {
      setErr((e as Error).message);
    }
  };

  const apply = () => {
    if (!result) return;
    useEditor.getState().edit((d) => {
      d.scenes = result.scenes;
      d.elements = result.elements;
      // keep background music; voice clips come from the shot list
      d.audio = [...d.audio.filter((c) => c.role === "music"), ...result.audio];
      d.duration = result.duration;
      d.defaults.gapBetweenAudio = gap;
    });
    useEditor.getState().set({ panel: null, selection: null, time: 0 });
  };

  const running = !!job && ["queued", "running"].includes(job.status);
  const missingAudio = [...new Set((text.match(/^\|[^|]*\|\s*([A-Za-z0-9_.-]+)\s*\|/gm) ?? []).map((l) => l.split("|")[2].trim()))]
    .filter((n) => n && !/^(audio|none|-+|—)$/i.test(n) && !audioNames.has(n.replace(/\.[^.]+$/, "").toLowerCase()));

  return (
    <div className="modal-back" onPointerDown={close}>
      <div className="modal import" onPointerDown={(e) => e.stopPropagation()}>
        <header>
          <h3>Import shot list</h3>
          <button className="icon" onClick={close}><X size={18} /></button>
        </header>
        {!result && (
          <>
            <p className="muted small">
              Upload the voiceovers (and any scene images named <code>S01.png</code>, <code>S02.png</code>, …) first. Then load the shot list: a Markdown table like
              <code> docs/shot-list.md</code>, a CSV, or JSON, with columns # · Audio · Cue · Visual · Overlay · Notes (optional: Image, Build, Outline, Fill, Text).
            </p>
            <div className="import-row">
              <input type="file" accept=".md,.markdown,.txt,.csv,.json" onChange={async (e) => {
                const f = e.target.files?.[0];
                if (!f) return;
                setFilename(f.name);
                setText(await f.text());
              }} />
              <label><input type="checkbox" checked={align} onChange={(e) => setAlign(e.target.checked)} /> Find each cue in the audio (speech recognition)</label>
              <label>Gap between voiceovers <input className="num short" type="number" step="0.1" min="0" value={gap} onChange={(e) => setGap(+e.target.value)} /> s</label>
            </div>
            <textarea className="import-text" placeholder="…or paste the shot list here" value={text} onChange={(e) => setText(e.target.value)} rows={12} />
            {missingAudio.length > 0 && (
              <p className="warn small"><TriangleAlert size={13} /> Not uploaded yet: {missingAudio.join(", ")}</p>
            )}
            <div className="analysis-actions">
              {running && <span className="muted small"><Loader2 size={13} className="spin" /> {job!.status === "queued" ? "Queued" : job!.message || "Working"} {Math.round(job!.progress * 100)}%</span>}
              <button className="primary" onClick={start} disabled={running || text.trim().length < 10}>Analyse shot list</button>
            </div>
            <p className="muted small">The first import downloads the speech model (about 150 MB); after that it takes roughly 3 s per minute of audio.</p>
          </>
        )}
        {err && <p className="bad">{err}</p>}
        {result && (
          <>
            <p>
              <strong>{result.scenes.length}</strong> scenes · <strong>{result.audio.length}</strong> voiceovers · {fmt(result.duration)} total
              {result.missingImages.length > 0 && <> · <span className="warn">{result.missingImages.length} without an image (placeholders)</span></>}
              {result.lowConfidence.length > 0 && <> · <span className="bad">check cues: {result.lowConfidence.join(", ")}</span></>}
            </p>
            {result.warnings.map((w) => <p key={w} className="warn small"><TriangleAlert size={13} /> {w}</p>)}
            <div className="import-table">
              <table>
                <thead><tr><th>#</th><th>Start</th><th>Length</th><th>Audio</th><th>Cue</th><th>Match</th><th>Image</th></tr></thead>
                <tbody>
                  {result.report.map((r) => (
                    <tr key={r.number}>
                      <td>{r.number}{r.build && <em className="muted"> build</em>}</td>
                      <td>{fmt(r.start)}</td>
                      <td>{r.duration.toFixed(1)} s</td>
                      <td className="muted">{r.audio ?? "—"}</td>
                      <td>{r.cue}</td>
                      <td className={r.score === null ? "muted" : r.score >= 0.8 ? "good" : r.score >= 0.6 ? "warn" : "bad"}>{r.score === null ? "—" : `${Math.round(r.score * 100)}%`}</td>
                      <td className={r.image ? "" : "muted"}>{r.image ?? "placeholder"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="analysis-actions">
              <span className="muted small">Replaces this project's scenes, elements and voice clips (background music stays). Undo reverts it.</span>
              <button onClick={() => setResult(null)}>Back</button>
              <button className="primary" onClick={apply}>Apply to project</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function fmt(t: number): string {
  return `${Math.floor(t / 60)}:${(t % 60).toFixed(1).padStart(4, "0")}`;
}
